<template>
  <div class="oegen-detail-page">
    <!-- 顶部操作栏 -->
    <div class="oegen-detail-header">
      <div class="oegen-detail-header-left">
        <el-button type="text" size="small" icon="el-icon-back" @click="back">返回列表</el-button>
        <span class="oegen-detail-title">
          <i class="el-icon-cpu"></i>
          任务详情
          <span class="oegen-detail-tid">#{{ tid }}</span>
        </span>
      </div>
    </div>

    <!-- 任务基本信息 -->
    <el-card shadow="never" class="oegen-detail-card">
      <div slot="header" class="oegen-detail-card-header">
        <i class="el-icon-document"></i>
        <span>任务信息</span>
      </div>
      <div v-if="detailLoading" class="oegen-detail-loading">
        <i class="el-icon-loading"></i>
        <p>正在加载...</p>
      </div>
      <el-descriptions v-if="task" :column="3" border size="small">
        <el-descriptions-item label="任务名称">{{ task.tname || '-' }}</el-descriptions-item>
        <el-descriptions-item label="数据文件">{{ task.dsrc || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ task.created_time || '-' }}</el-descriptions-item>
        <el-descriptions-item label="开始时间">{{ task.start_time || '-' }}</el-descriptions-item>
        <el-descriptions-item label="结束时间">{{ task.end_time || '-' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag size="mini" :type="taskStatusType(task)">{{ taskStatusText(task) }}</el-tag>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 运行控制 -->
    <el-card shadow="never" class="oegen-detail-card">
      <div slot="header" class="oegen-detail-card-header">
        <i class="el-icon-video-play"></i>
        <span>运行控制</span>
      </div>

      <div class="run-control">
        <!-- 第一行：模型选择 + 回答字数 + 运行操作按钮 -->
        <div class="run-row run-row--action">
          <div class="run-field run-field--model">
            <label class="run-field-label">
              <i class="el-icon-cpu"></i>
              <span>模型</span>
            </label>
            <el-select
              v-model="runForm.model"
              placeholder="请选择大模型"
              clearable
              filterable
              size="small"
              class="run-model-select"
            >
              <el-option-group
                v-for="group in modelGroups"
                :key="group.label"
                :label="group.label"
              >
                <el-option
                  v-for="model in group.options"
                  :key="model.key"
                  :label="model.name"
                  :value="model.key"
                ></el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="run-field run-field--word-count">
            <label class="run-field-label">
              <i class="el-icon-edit-outline"></i>
              <span>回答字数</span>
              <span class="run-field-hint" v-if="!wordCountEditing">点击编辑</span>
              <span class="run-field-hint" v-else>按 Enter 或失焦保存</span>
            </label>
            <!-- 非编辑状态：显示为可点击的文本 -->
            <div
              v-if="!wordCountEditing"
              class="word-count-display"
              @click="startEditWordCount"
            >
              <span class="word-count-text">{{ runForm.wordCount || defaultWordCount }}</span>
              <i class="el-icon-edit"></i>
            </div>
            <!-- 编辑状态：输入框 -->
            <el-input
              v-else
              v-model="runForm.wordCount"
              size="small"
              :placeholder="defaultWordCount"
              class="run-word-count-input"
              @blur="saveWordCount"
              @keyup.enter.native="saveWordCount"
              ref="wordCountInput"
            ></el-input>
          </div>
          <div class="run-action-buttons">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-video-play"
              :loading="runLoading"
              :disabled="running"
              @click="runTask"
            >{{ running ? '运行中...' : '运行任务' }}</el-button>
          </div>
        </div>

        <!-- 第二行：User Prompt 编辑区（可折叠，默认收起） -->
        <div class="run-row run-row--prompt">
          <div class="run-field run-field--prompt">
            <!-- 折叠态：精致状态条 -->
            <div class="prompt-summary" v-if="!promptExpanded" @click="expandPrompt">
              <div class="prompt-summary-left">
                <i class="el-icon-chat-line-square prompt-summary-icon"></i>
                <span class="prompt-summary-label">User Prompt</span>
                <span class="prompt-summary-badge" :class="promptBadgeClass">
                  <i :class="promptBadgeIcon"></i>
                  {{ promptBadgeText }}
                </span>
              </div>
              <div class="prompt-summary-right">
                <span class="prompt-summary-action">展开编辑</span>
                <i class="el-icon-arrow-down prompt-summary-arrow"></i>
              </div>
            </div>
            <!-- 展开态：完整编辑器 -->
            <transition name="prompt-expand">
              <div class="prompt-editor" v-if="promptExpanded">
                <div class="run-field-header">
                  <label class="run-field-label">
                    <i class="el-icon-chat-line-square"></i>
                    <span>User Prompt</span>
                    <span class="run-field-hint">留空使用内置默认模板；填写则作为补充追加到默认模板末尾</span>
                  </label>
                  <div class="prompt-editor-actions">
                    <el-button
                      type="primary"
                      size="small"
                      plain
                      icon="el-icon-document-add"
                      :loading="configSaving"
                      @click="saveConfig"
                    >保存</el-button>
                    <el-button
                      size="small"
                      icon="el-icon-arrow-up"
                      @click="collapsePrompt"
                    >收起</el-button>
                  </div>
                </div>
                <el-input
                  v-model="runForm.userPrompt"
                  type="textarea"
                  :rows="5"
                  placeholder="请输入用户提示词模板..."
                  class="run-prompt-input"
                ></el-input>
              </div>
            </transition>
          </div>
        </div>

        <!-- 进度条：仅运行中显示，完成后自动隐藏 -->
        <div v-if="running && task && task.total > 0" class="oegen-progress-wrap">
          <el-progress
            :percentage="Number(task.progress) || 0"
            :status="progressStatus"
            :stroke-width="18"
            :text-inside="true"
          ></el-progress>
          <div class="oegen-progress-text">
            已完成 {{ task.done || 0 }} / {{ task.total || 0 }} 个单元格
          </div>
        </div>
      </div>
    </el-card>

    <!-- 生成结果 -->
    <el-card shadow="never" class="oegen-detail-card">
      <div slot="header" class="oegen-detail-card-header">
        <i class="el-icon-tickets"></i>
        <span>生成结果</span>
        <div class="oegen-result-toolbar">
          <el-input
            v-model="qidFilter"
            placeholder="按题号 qid 过滤"
            clearable
            size="small"
            style="width: 160px; margin-right: 8px;"
            @keyup.enter.native="loadResults(1)"
            @clear="loadResults(1)"
          ></el-input>
          <el-button type="primary" size="small" icon="el-icon-search" @click="loadResults(1)">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="loadResults(currentPage)">刷新</el-button>
          <el-button type="danger" size="small" icon="el-icon-delete" plain @click="clearAllResults">全部清除</el-button>
          <el-button size="small" icon="el-icon-download" :loading="downLoading" @click="downloadCheck">下载检查</el-button>
          <el-upload
            :show-file-list="false"
            accept=".xlsx,.xls"
            :before-upload="beforeImportUpload"
            action=""
            style="display: inline-block; margin-left: 8px;"
          >
            <el-button type="warning" size="small" plain icon="el-icon-upload2" :loading="importLoading">导入检查</el-button>
          </el-upload>
          <el-button
            type="primary"
            size="small"
            plain
            icon="el-icon-magic-stick"
            :loading="rawdataLoading"
            @click="downloadRawdata"
            style="margin-left: 8px;"
          >贴入句子并下载rawdata</el-button>
          <span
            v-if="failedCount > 0"
            class="oegen-failed-chip"
            :class="{ 'is-active': showFailedOnly }"
            @click="toggleFailedFilter"
            :title="showFailedOnly ? '点击清除失败筛选，显示全部记录' : '点击仅显示失败记录'"
          >
            <i :class="showFailedOnly ? 'el-icon-circle-close' : 'el-icon-warning-outline'"></i>
            <span v-if="showFailedOnly" class="oegen-failed-chip-label">清除失败筛选</span>
            <span v-else class="oegen-failed-chip-label">失败：{{ failedCount }} 个</span>
          </span>
        </div>
      </div>
      <el-table
        :data="resultPage"
        size="small"
        stripe
        v-loading="resultLoading"
        :row-class-name="followRowClassName"
        :header-cell-style="{ background: '#fafbfc', color: '#606266', fontWeight: 600 }"
      >
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="oegen-follow-wrap" v-if="getFollowRows(scope.row).length">
              <div class="oegen-follow-title">
                <i class="el-icon-chat-line-square"></i>
                <span>追问数据</span>
                <span class="oegen-follow-count">{{ getFollowRows(scope.row).length }} 条</span>
              </div>
              <el-table
                :data="getFollowRows(scope.row)"
                size="mini"
                border
                :header-cell-style="{ background: '#f4f6fa', color: '#606266' }"
              >
                <el-table-column label="轮次" prop="follow_seq" width="70" align="center">
                  <template slot-scope="sub">
                    <el-tag size="mini" type="warning">第{{ sub.row.follow_seq }}轮</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="追问问题" prop="follow_question" min-width="220" show-overflow-tooltip>
                  <template slot-scope="sub">
                    <span v-if="sub.row.follow_question">{{ sub.row.follow_question }}</span>
                    <span v-else class="oegen-empty-cell">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="AI回答" prop="aitxt" min-width="240" show-overflow-tooltip>
                  <template slot-scope="sub">
                    <span v-if="sub.row.aitxt" class="oegen-aitxt">{{ sub.row.aitxt }}</span>
                    <span v-else class="oegen-empty-cell">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="中文翻译" prop="aitxt_zh" min-width="240" show-overflow-tooltip>
                  <template slot-scope="sub">
                    <span v-if="sub.row.aitxt_zh" class="oegen-aitxt">{{ sub.row.aitxt_zh }}</span>
                    <span v-else class="oegen-empty-cell">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="状态" width="70" align="center">
                  <template slot-scope="sub">
                    <el-tag size="mini" :type="sub.row.status == 1 ? 'success' : 'danger'">
                      {{ sub.row.status == 1 ? '成功' : '失败' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="生成时间" prop="created_time" width="145" align="center"></el-table-column>
                <el-table-column label="操作" width="100" align="center">
                  <template slot-scope="sub">
                    <div class="oegen-row-actions">
                      <el-tooltip content="编辑追问内容" placement="top">
                        <button class="oegen-action-btn oegen-action-btn--edit" @click="openEditFollow(sub.row)">
                          <i class="el-icon-edit"></i>
                        </button>
                      </el-tooltip>
                      <el-tooltip content="删除该条追问" placement="top">
                        <button class="oegen-action-btn oegen-action-btn--delete" @click="deleteFollow(sub.row)">
                          <i class="el-icon-delete"></i>
                        </button>
                      </el-tooltip>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div v-else class="oegen-follow-empty">
              <i class="el-icon-info"></i> 暂无追问数据
            </div>
          </template>
        </el-table-column>
        <el-table-column label="题号" width="110" align="center">
          <template slot-scope="scope">
            <div class="oegen-vid-cell">
              <span class="oegen-vid-text">{{ scope.row.vid }}</span>
              <el-tooltip
                v-if="getFollowRows(scope.row).length"
                :content="'有 ' + getFollowRows(scope.row).length + ' 条追问数据，点击左侧箭头展开'"
                placement="top"
              >
                <span class="oegen-follow-badge">
                  <i class="el-icon-chat-line-square"></i>
                  {{ getFollowRows(scope.row).length }}
                </span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="样本行" prop="respid" width="80" align="center"></el-table-column>
        <el-table-column label="语言" prop="language" width="90" show-overflow-tooltip></el-table-column>
        <el-table-column label="AI生成内容" prop="aitxt" min-width="280" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.aitxt" class="oegen-aitxt">{{ scope.row.aitxt }}</span>
            <span v-else class="oegen-empty-cell">-</span>
          </template>
        </el-table-column>
        <el-table-column label="翻译文本" prop="aitxt_zh" min-width="280" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.aitxt_zh" class="oegen-aitxt">{{ scope.row.aitxt_zh }}</span>
            <span v-else class="oegen-empty-cell">-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center" sortable :sort-method="sortStatus" :sort-orders="['ascending', 'descending']">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.status != 1"
              :content="scope.row.err_msg || '失败原因未知'"
              placement="top"
              effect="dark"
            >
              <el-tag size="mini" type="danger" class="oegen-status-failed">失败</el-tag>
            </el-tooltip>
            <el-tag v-else size="mini" type="success">成功</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="生成时间" prop="created_time" width="155" align="center"></el-table-column>
        <el-table-column label="操作" width="110" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="oegen-row-actions">
              <el-tooltip content="编辑 AI 生成内容" placement="top">
                <button class="oegen-action-btn oegen-action-btn--edit" @click="openEditMain(scope.row)">
                  <i class="el-icon-edit"></i>
                </button>
              </el-tooltip>
              <el-tooltip content="删除该行" placement="top">
                <button class="oegen-action-btn oegen-action-btn--delete" @click="deleteMain(scope.row)">
                  <i class="el-icon-delete"></i>
                </button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div class="oegen-pagination" v-if="filteredResults.length > pageSize">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :total="filteredResults.length"
          :current-page.sync="currentPage"
          :page-size="pageSize"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 编辑主结果对话框 -->
    <el-dialog
      :visible.sync="mainEditVisible"
      :show-close="false"
      width="640px"
      custom-class="oegen-edit-dialog"
      append-to-body
    >
      <div slot="title" class="oegen-edit-dialog-header">
        <div class="oegen-edit-dialog-icon oegen-edit-dialog-icon--main">
          <i class="el-icon-edit-outline"></i>
        </div>
        <div class="oegen-edit-dialog-title">
          <span class="oegen-edit-dialog-title-main">编辑 AI 生成内容</span>
          <span class="oegen-edit-dialog-title-sub" v-if="mainEditForm.row">
            ID: {{ mainEditForm.row.id }} · 题号 {{ mainEditForm.row.vid }} · 样本 {{ mainEditForm.row.respid }} · {{ mainEditForm.row.language || '-' }}
          </span>
        </div>
      </div>
      <div class="oegen-edit-dialog-body" v-if="mainEditForm.row">
        <div class="oegen-edit-field">
          <label class="oegen-edit-field-label">
            <i class="el-icon-chat-dot-square"></i>
            <span>AI 生成内容</span>
          </label>
          <el-input
            v-model="mainEditForm.aitxt"
            type="textarea"
            :rows="10"
            placeholder="请输入 AI 生成内容..."
            class="oegen-edit-textarea"
          ></el-input>
          <div class="oegen-edit-field-footer">
            <span class="oegen-edit-char-count">{{ (mainEditForm.aitxt || '').length }} 字符</span>
          </div>
        </div>
      </div>
      <div slot="footer" class="oegen-edit-dialog-footer">
        <el-button size="small" @click="mainEditVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="mainEditSaving" icon="el-icon-check" @click="saveMainEdit">保存修改</el-button>
      </div>
    </el-dialog>

    <!-- 编辑追问结果对话框 -->
    <el-dialog
      :visible.sync="followEditVisible"
      :show-close="false"
      width="640px"
      custom-class="oegen-edit-dialog"
      append-to-body
    >
      <div slot="title" class="oegen-edit-dialog-header">
        <div class="oegen-edit-dialog-icon oegen-edit-dialog-icon--follow">
          <i class="el-icon-chat-line-square"></i>
        </div>
        <div class="oegen-edit-dialog-title">
          <span class="oegen-edit-dialog-title-main">编辑追问内容</span>
          <span class="oegen-edit-dialog-title-sub" v-if="followEditForm.row">
            第 {{ followEditForm.row.follow_seq }} 轮追问 · 题号 {{ followEditForm.row.base_vid }} · 样本 {{ followEditForm.row.respid }}
          </span>
        </div>
      </div>
      <div class="oegen-edit-dialog-body" v-if="followEditForm.row">
        <div class="oegen-edit-field">
          <label class="oegen-edit-field-label">
            <i class="el-icon-question"></i>
            <span>追问问题</span>
          </label>
          <el-input
            v-model="followEditForm.follow_question"
            type="textarea"
            :rows="3"
            placeholder="请输入追问问题..."
            class="oegen-edit-textarea"
          ></el-input>
        </div>
        <div class="oegen-edit-field">
          <label class="oegen-edit-field-label">
            <i class="el-icon-chat-dot-square"></i>
            <span>AI 回答</span>
          </label>
          <el-input
            v-model="followEditForm.aitxt"
            type="textarea"
            :rows="8"
            placeholder="请输入 AI 回答..."
            class="oegen-edit-textarea"
          ></el-input>
          <div class="oegen-edit-field-footer">
            <span class="oegen-edit-char-count">{{ (followEditForm.aitxt || '').length }} 字符</span>
          </div>
        </div>
      </div>
      <div slot="footer" class="oegen-edit-dialog-footer">
        <el-button size="small" @click="followEditVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="followEditSaving" icon="el-icon-check" @click="saveFollowEdit">保存修改</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "OeGenDetail",
  props: ["tid"],
  data() {
    return {
      task: null,
      detailLoading: true,
      // 运行控制
      runForm: {
        model: "",
        userPrompt: "",
        wordCount: "",
      },
      runLoading: false,
      running: false,
      wordCountEditing: false,
      wordCountOriginal: "",
      promptExpanded: false,
      pollTimer: null,
      // 回答字数默认值（与 Go 端 defaultWordCount 保持一致）
      defaultWordCount: "每题 20-30 字",
      // 模型列表
      modelList: [],
      configSaving: false,
      // 结果
      results: [],
      resultLoading: false,
      qidFilter: "",
      currentPage: 1,
      pageSize: 20,
      // 仅显示失败记录开关
      showFailedOnly: false,
      // 追问数据：按 respid + base_vid 分组，值为主结果对应的追问行数组
      followMap: {},
      // 下载检查
      downLoading: false,
      // 导入检查
      importLoading: false,
      // 贴入句子并下载rawdata
      rawdataLoading: false,
      // 编辑主结果
      mainEditVisible: false,
      mainEditSaving: false,
      mainEditForm: { row: null, aitxt: "" },
      // 编辑追问
      followEditVisible: false,
      followEditSaving: false,
      followEditForm: { row: null, follow_question: "", aitxt: "" },
    };
  },
  computed: {
    // 过滤后的结果列表：开启失败筛选时仅显示 status != 1 的记录
    filteredResults() {
      if (!this.showFailedOnly) return this.results;
      return this.results.filter(function (r) {
        return r.status != 1;
      });
    },
    resultPage() {
      var list = this.filteredResults;
      var start = (this.currentPage - 1) * this.pageSize;
      return list.slice(start, start + this.pageSize);
    },
    // 失败数量：主结果中 status != 1 的行数
    failedCount() {
      if (!this.results || !this.results.length) return 0;
      return this.results.filter(function (r) {
        return r.status != 1;
      }).length;
    },
    progressStatus() {
      if (!this.task) return "";
      if (this.task.status == 1) return "success";
      if (this.task.progress >= 100) return "success";
      return "";
    },
    // Prompt 折叠态徽标：根据是否已填写显示不同状态
    promptBadgeClass() {
      return this.runForm.userPrompt && this.runForm.userPrompt.trim()
        ? "is-custom"
        : "is-default";
    },
    promptBadgeText() {
      return this.runForm.userPrompt && this.runForm.userPrompt.trim()
        ? "已自定义"
        : "使用默认模板";
    },
    promptBadgeIcon() {
      return this.runForm.userPrompt && this.runForm.userPrompt.trim()
        ? "el-icon-circle-check"
        : "el-icon-info";
    },
    modelGroups() {
      // 供应商显示名称映射（与 Go 端 providerDisplayName 保持一致）
      var providerNames = {
        claude: "Claude",
        ark: "火山引擎",
        deepseek: "DeepSeek",
        qianfan: "百度千帆",
      };
      var groups = {};
      var order = [];
      this.modelList.forEach(function (m) {
        var provider = m.provider || "其他";
        if (!groups[provider]) {
          groups[provider] = { label: providerNames[provider] || provider, options: [] };
          order.push(provider);
        }
        groups[provider].options.push(m);
      });
      return order.map(function (k) {
        return groups[k];
      });
    },
  },
  created() {
    this.loadDetail();
    this.loadModelList();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    // 展开 / 收起 Prompt 编辑区
    expandPrompt() {
      this.promptExpanded = true;
    },
    collapsePrompt() {
      this.promptExpanded = false;
    },

    // 加载任务详情（PHP 直查库）
    loadDetail() {
      this.detailLoading = true;
      var api = this.COMMON.httpUrl + "oegenTaskDetail";
      this.axios.post(api, { id: this.tid }).then((response) => {
        var rs = response.data;
        this.detailLoading = false;
        if (rs.code) {
          this.$message.error(rs.message);
        } else {
          this.task = rs.data || null;
          // 从任务记录中恢复 prompt 和模型配置
          if (this.task) {
            this.runForm.userPrompt = this.task.user_prompt || "";
            this.runForm.model = this.task.model_key || "";
            this.runForm.wordCount = this.task.word_count || "";
          }
          // 如果任务正在进行中（status==0 且已启动），恢复轮询，保持进度条状态
          if (this.task && this.isRunning(this.task)) {
            this.running = true;
            this.startPolling();
          }
          this.loadResults();
        }
      }).catch(() => {
        this.detailLoading = false;
        this.$message.error("加载任务详情失败");
      });
    },

    // 判断任务是否正在运行：status==0 且已有 start_time（已启动过）
    // 用于刷新页面后恢复轮询和进度条状态
    isRunning(task) {
      if (!task) return false;
      if (task.status != 0) return false;
      // start_time 非空说明任务已启动（无论 total 是否已初始化）
      return !!task.start_time;
    },

    // 任务状态文案：0=待运行/运行中, 1=已完成, 2=运行失败
    taskStatusText(task) {
      if (!task) return '-';
      if (task.status == 1) return '已完成';
      if (task.status == 2) return '运行失败';
      // status=0：start_time 为空=待运行，非空=运行中
      return task.start_time ? '运行中' : '待运行';
    },

    // 任务状态 el-tag 类型
    taskStatusType(task) {
      if (!task) return 'info';
      if (task.status == 1) return 'success';
      if (task.status == 2) return 'danger';
      return task.start_time ? 'warning' : 'info';
    },

    // 加载模型列表（仅获取模型选项，不再读取配置）
    loadModelList() {
      var api = this.COMMON.httpUrl + "oegenGetModelList";
      this.axios.post(api, {}).then((response) => {
        var rs = response.data;
        if (!rs.code) {
          this.modelList = rs.data || [];
        }
      }).catch(() => {});
    },

    // 保存 prompt 配置（更新到 cx_oegen_task 表）
    saveConfig() {
      this.configSaving = true;
      var api = this.COMMON.httpUrl + "oegenSaveConfig";
      var params = {
        tid: this.tid,
        user_prompt: this.runForm.userPrompt,
        model_key: this.runForm.model,
        word_count: this.runForm.wordCount,
      };
      this.axios.post(api, params).then((response) => {
        var rs = response.data;
        this.configSaving = false;
        if (rs.code) {
          this.$message.error(rs.message);
        } else {
          this.$message.success("Prompt 保存成功");
        }
      }).catch(() => {
        this.configSaving = false;
        this.$message.error("保存失败");
      });
    },

    // 开始编辑回答字数
    startEditWordCount() {
      // 默认状态下点击，预填充默认值，便于用户在默认基础上修改
      if (!this.runForm.wordCount) {
        this.runForm.wordCount = this.defaultWordCount;
      }
      // 记录编辑前的原始值，用于保存时判断是否有过修改
      this.wordCountOriginal = this.runForm.wordCount;
      this.wordCountEditing = true;
      this.$nextTick(() => {
        if (this.$refs.wordCountInput) {
          this.$refs.wordCountInput.focus();
        }
      });
    },

    // 保存回答字数并退出编辑状态
    saveWordCount() {
      this.wordCountEditing = false;
      // 未做任何修改则不保存
      if (this.runForm.wordCount === this.wordCountOriginal) {
        return;
      }
      var api = this.COMMON.httpUrl + "oegenSaveConfig";
      var params = {
        tid: this.tid,
        word_count: this.runForm.wordCount,
      };
      this.axios.post(api, params).then((response) => {
        var rs = response.data;
        if (!rs.code) {
          this.$message.success("回答字数已保存");
        }
      }).catch(() => {});
    },

    // 运行任务（PHP 代理转发到 Go /oegen/run）
    // model / user_prompt / word_count 直接传给 Go，并发数由 Go 端硬编码
    runTask() {
      this.runLoading = true;
      var api = this.COMMON.httpUrl + "oegenTaskRun";
      var params = { tid: this.tid };
      if (this.runForm.model && this.runForm.model.trim()) {
        params.model = this.runForm.model.trim();
      }
      if (this.runForm.userPrompt && this.runForm.userPrompt.trim()) {
        params.user_prompt = this.runForm.userPrompt.trim();
      }
      if (this.runForm.wordCount && this.runForm.wordCount.trim()) {
        params.word_count = this.runForm.wordCount.trim();
      }
      this.axios.post(api, params).then((response) => {
        var rs = response.data;
        this.runLoading = false;
        if (rs.code) {
          this.$message.error(rs.message);
        } else {
          this.$message.success("任务已启动");
          this.running = true;
          this.loadStatus();
          this.startPolling();
        }
      }).catch(() => {
        this.runLoading = false;
        this.$message.error("启动任务失败");
      });
    },

    // 查询进度（PHP 代理转发到 Go /oegen/status）
    loadStatus() {
      var api = this.COMMON.httpUrl + "oegenTaskStatus";
      this.axios.post(api, { tid: this.tid }).then((response) => {
        var rs = response.data;
        if (!rs.code && rs.data) {
          this.task = Object.assign({}, this.task, rs.data);
          // 任务失败（AI 欠费/限流/网络等服务商级错误）：弹窗提示并停止轮询
          // err_msg 由 Go 端 FailOegenTask 写入，同时已入库 cx_message 系统消息
          if (this.task.status == 2 || (this.task.err_msg && this.task.err_msg.trim())) {
            this.running = false;
            this.stopPolling();
            this.$message({
              message: this.task.err_msg || "任务运行失败",
              type: "error",
              duration: 10000,
              showClose: true,
            });
            this.loadResults();
            return;
          }
          if (this.task.status == 1 || Number(this.task.progress) >= 100) {
            this.running = false;
            this.stopPolling();
            this.loadResults();
          }
        }
      }).catch(() => {});
    },

    // 轮询进度（每隔 1 秒）
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        this.loadStatus();
      }, 1000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    // 加载结果（PHP 代理转发到 Go /oegen/result）
    loadResults(page) {
      if (page) this.currentPage = page;
      this.resultLoading = true;
      var api = this.COMMON.httpUrl + "oegenTaskResult";
      var params = { tid: this.tid };
      if (this.qidFilter && this.qidFilter.trim()) {
        params.qid = this.qidFilter.trim();
      }
      this.axios.post(api, params).then((response) => {
        var rs = response.data;
        this.resultLoading = false;
        if (!rs.code) {
          this.results = rs.data || [];
          if (this.currentPage > Math.ceil(this.filteredResults.length / this.pageSize)) {
            this.currentPage = 1;
          }
          // 加载追问数据（与主结果一并刷新）
          this.loadFollowResults();
        }
      }).catch(() => {
        this.resultLoading = false;
        this.$message.error("加载结果失败");
      });
    },

    // 切换“仅显示失败记录”筛选：开启后列表只显示 status != 1 的记录，再次点击恢复全部
    toggleFailedFilter() {
      this.showFailedOnly = !this.showFailedOnly;
      this.currentPage = 1;
    },

    // 加载追问结果（一次性拉取整个任务的追问数据，按 respid + base_vid 分组）
    loadFollowResults() {
      var api = this.COMMON.httpUrl + "oegenFollowResult";
      this.axios.post(api, { tid: this.tid }).then((response) => {
        var rs = response.data;
        if (!rs.code) {
          var list = rs.data || [];
          var map = {};
          list.forEach(function (item) {
            var key = item.respid + "_" + item.base_vid;
            if (!map[key]) {
              map[key] = [];
            }
            map[key].push(item);
          });
          this.followMap = map;
          // 重新赋值 results 引用，触发 el-table 重新渲染，
          // 使 row-class-name 重算（控制无追问数据行的展开图标隐藏）
          this.results = this.results.slice();
        }
      }).catch(() => {});
    },

    // 获取某行主结果对应的追问数据
    getFollowRows(row) {
      var key = row.respid + "_" + row.vid;
      return this.followMap[key] || [];
    },

    // 状态列排序：失败（status!=1）排前，成功排后；升降序互换
    sortStatus(a, b) {
      var av = a.status == 1 ? 1 : 0;
      var bv = b.status == 1 ? 1 : 0;
      return av - bv;
    },

    // 行样式：为有/无追问数据的行添加标记类，用于 CSS 控制展开图标显示
    followRowClassName({ row }) {
      var key = row.respid + "_" + row.vid;
      return this.followMap[key] && this.followMap[key].length ? "has-follow" : "no-follow";
    },

    // 全部清除：删除该任务的所有生成结果
    clearAllResults() {
      this.$confirm("确认清除该任务的所有生成结果？此操作不可恢复。", "清除确认", {
        confirmButtonText: "确定清除",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        var api = this.COMMON.httpUrl + "oegenResultClear";
        this.axios.post(api, { tid: this.tid }).then((response) => {
          var rs = response.data;
          if (rs.code) {
            this.$message.error(rs.message);
          } else {
            this.$message.success("已清除全部结果");
            this.results = [];
            this.followMap = {};
            this.currentPage = 1;
            // 如果任务已完成，重置任务进度显示
            if (this.task && this.task.status == 1) {
              this.task = Object.assign({}, this.task, { done: 0, total: 0, progress: 0 });
            }
          }
        }).catch(() => {
          this.$message.error("清除失败");
        });
      }).catch(() => {});
    },

    // ===== 主结果行：编辑 / 删除 =====
    // 打开编辑对话框
    openEditMain(row) {
      this.mainEditForm = {
        row: row,
        aitxt: row.aitxt || "",
      };
      this.mainEditVisible = true;
    },

    // 保存主结果编辑
    saveMainEdit() {
      if (!this.mainEditForm.row) return;
      this.mainEditSaving = true;
      var api = this.COMMON.httpUrl + "oegenResultEdit";
      this.axios.post(api, {
        id: this.mainEditForm.row.id,
        aitxt: this.mainEditForm.aitxt,
      }).then((response) => {
        var rs = response.data;
        this.mainEditSaving = false;
        if (rs.code) {
          this.$message.error(rs.message);
        } else {
          // 本地同步更新
          var idx = this.results.findIndex(function (r) {
            return r.id === this.mainEditForm.row.id;
          }.bind(this));
          if (idx > -1) {
            this.results[idx].aitxt = this.mainEditForm.aitxt;
            this.results = this.results.slice();
          }
          this.$message.success(rs.message || "保存成功");
          this.mainEditVisible = false;
        }
      }).catch(() => {
        this.mainEditSaving = false;
        this.$message.error("保存失败");
      });
    },

    // 删除主结果行（含追问级联删除）
    deleteMain(row) {
      var followCount = this.getFollowRows(row).length;
      var tip = followCount > 0
        ? '该行包含 <strong style="color:#e6a23c">' + followCount + ' 条追问数据</strong>，将一并删除。'
        : '删除后无法恢复。';
      var confirmText = followCount > 0 ? '确认删除' : '删除';
      this.$confirm(
        '<div style="line-height:1.7">' +
        '<p style="margin:0 0 8px;color:#303133;font-weight:500">确认删除题号 <span style="color:#409EFF;font-weight:600">' + row.vid + '</span>（样本 ' + row.respid + '）的 AI 生成内容？</p>' +
        '<p style="margin:0;color:#909399;font-size:12px">' + tip + '</p>' +
        '</div>',
        '删除确认',
        {
          confirmButtonText: confirmText,
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true,
        }
      ).then(() => {
        var api = this.COMMON.httpUrl + "oegenResultDel";
        this.axios.post(api, { id: row.id }).then((response) => {
          var rs = response.data;
          if (rs.code) {
            this.$message.error(rs.message);
          } else {
            // 本地移除该行
            this.results = this.results.filter(function (r) {
              return r.id !== row.id;
            });
            // 移除追问缓存
            if (followCount > 0) {
              var key = row.respid + "_" + row.vid;
              this.$delete(this.followMap, key);
            }
            this.results = this.results.slice();
            this.$message.success(
              followCount > 0
                ? '已删除该行及 ' + followCount + ' 条追问数据'
                : '删除成功'
            );
          }
        }).catch(() => {
          this.$message.error("删除失败");
        });
      }).catch(() => {});
    },

    // ===== 追问结果行：编辑 / 删除 =====
    // 打开追问编辑对话框
    openEditFollow(row) {
      this.followEditForm = {
        row: row,
        follow_question: row.follow_question || "",
        aitxt: row.aitxt || "",
      };
      this.followEditVisible = true;
    },

    // 保存追问编辑
    saveFollowEdit() {
      if (!this.followEditForm.row) return;
      this.followEditSaving = true;
      var api = this.COMMON.httpUrl + "oegenFollowEdit";
      this.axios.post(api, {
        id: this.followEditForm.row.id,
        follow_question: this.followEditForm.follow_question,
        aitxt: this.followEditForm.aitxt,
      }).then((response) => {
        var rs = response.data;
        this.followEditSaving = false;
        if (rs.code) {
          this.$message.error(rs.message);
        } else {
          // 本地同步更新 followMap
          var baseVid = this.followEditForm.row.base_vid;
          var respid = this.followEditForm.row.respid;
          var key = respid + "_" + baseVid;
          if (this.followMap[key]) {
            var idx = this.followMap[key].findIndex(function (r) {
              return r.id === this.followEditForm.row.id;
            }.bind(this));
            if (idx > -1) {
              this.followMap[key][idx].follow_question = this.followEditForm.follow_question;
              this.followMap[key][idx].aitxt = this.followEditForm.aitxt;
              this.followMap = Object.assign({}, this.followMap);
            }
          }
          this.$message.success(rs.message || "保存成功");
          this.followEditVisible = false;
        }
      }).catch(() => {
        this.followEditSaving = false;
        this.$message.error("保存失败");
      });
    },

    // 删除单条追问
    deleteFollow(row) {
      this.$confirm(
        '<div style="line-height:1.7">' +
        '<p style="margin:0 0 8px;color:#303133;font-weight:500">确认删除第 <span style="color:#e6a23c;font-weight:600">' + row.follow_seq + '</span> 轮追问？</p>' +
        '<p style="margin:0;color:#909399;font-size:12px">题号 ' + row.base_vid + ' · 样本 ' + row.respid + '，删除后无法恢复。</p>' +
        '</div>',
        '删除确认',
        {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true,
        }
      ).then(() => {
        var api = this.COMMON.httpUrl + "oegenFollowDel";
        this.axios.post(api, { id: row.id }).then((response) => {
          var rs = response.data;
          if (rs.code) {
            this.$message.error(rs.message);
          } else {
            // 本地移除追问
            var key = row.respid + "_" + row.base_vid;
            if (this.followMap[key]) {
              this.followMap[key] = this.followMap[key].filter(function (r) {
                return r.id !== row.id;
              });
              if (this.followMap[key].length === 0) {
                this.$delete(this.followMap, key);
              } else {
                this.followMap = Object.assign({}, this.followMap);
              }
            }
            // 触发主表重渲染以更新追问徽章
            this.results = this.results.slice();
            this.$message.success("删除成功");
          }
        }).catch(() => {
          this.$message.error("删除失败");
        });
      }).catch(() => {});
    },

    // 下载检查：下载 Excel（Go 端流式生成，含 oe / error sheet）
    // 文件名使用任务名称（tname），去除文件名非法字符；为空时回退为 oe
    downloadCheck() {
      if (!this.tid) {
        this.$message.warning("请先选择任务");
        return;
      }
      this.downLoading = true;
      var token = "Bearer " + localStorage.getItem("token");
      var src = this.COMMON.httpUrl + "oegenResultDown";
      fetch(src, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ tid: String(this.tid) }),
      })
        .then((response) => {
          this.downLoading = false;
          if (!response.ok) {
            return response.json().then((errorData) => {
              throw new Error(errorData.message || "下载异常");
            });
          }
          return response.blob();
        })
        .then((blob) => {
          // 文件名取任务名称，剔除 Windows 文件名非法字符 / \ : * ? " < > |
          var name = (this.task && this.task.tname ? String(this.task.tname) : "").trim();
          if (!name) {
            name = "oe";
          }
          name = name.replace(/[/\\:*?"<>|]/g, "_");
          var url = window.URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.download = name + ".xlsx";
          a.href = url;
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          this.$message.error(error.message || "下载失败");
        });
    },

    // 贴入句子并下载rawdata：将 oegen_result 的 aitxt 贴入 rawdata 对应 (respid, vid) 单元格并下载
    // 单元格不含"严格落实"的记录会写入 error sheet；文件名 = 任务名 + "rawdata"
    downloadRawdata() {
      if (!this.tid) {
        this.$message.warning("请先选择任务");
        return;
      }
      this.rawdataLoading = true;
      var token = "Bearer " + localStorage.getItem("token");
      var src = this.COMMON.httpUrl + "oegenRawdataDown";
      fetch(src, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ tid: String(this.tid) }),
      })
        .then((response) => {
          this.rawdataLoading = false;
          if (!response.ok) {
            return response.json().then((errorData) => {
              throw new Error(errorData.message || "下载异常");
            });
          }
          return response.blob();
        })
        .then((blob) => {
          // 文件名 = 任务名 + "rawdata"，剔除 Windows 文件名非法字符
          var name = (this.task && this.task.tname ? String(this.task.tname) : "").trim();
          if (!name) {
            name = "rawdata";
          } else {
            name = name + "rawdata";
          }
          name = name.replace(/[/\\:*?"<>|]/g, "_");
          var url = window.URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.download = name + ".xlsx";
          a.href = url;
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          this.$message.error(error.message || "下载失败");
        });
    },

    // 导入检查：el-upload 的 before-upload 钩子，返回 false 阻止自动上传，改用 fetch 手动上传
    beforeImportUpload(file) {
      this.importCheck(file);
      return false;
    },

    // 导入检查：上传校对后的 Excel 到 PHP oegenResultImport，返回汇总后刷新列表
    importCheck(file) {
      if (!this.tid) {
        this.$message.warning("请先选择任务");
        return;
      }
      this.importLoading = true;
      var token = "Bearer " + localStorage.getItem("token");
      var formData = new FormData();
      formData.append("tid", String(this.tid));
      formData.append("file", file);
      var src = this.COMMON.httpUrl + "oegenResultImport";
      fetch(src, {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((rs) => {
          this.importLoading = false;
          if (rs.code) {
            this.$message.error(rs.message || "导入失败");
            return;
          }
          var d = rs.data || {};
          var msg =
            "导入完成：原句更新 " +
            (d.base_updated || 0) +
            " 条，追问更新 " +
            (d.follow_updated || 0) +
            " 条，新增追问 " +
            (d.follow_inserted || 0) +
            " 条，跳过 " +
            (d.skipped || 0) +
            " 行";
          if (d.errors && d.errors.length) {
            msg += "；错误 " + d.errors.length + " 条";
          }
          this.$message.success(msg);
          this.loadResults();
        })
        .catch(() => {
          this.importLoading = false;
          this.$message.error("导入失败");
        });
    },

    back() {
      this.$router.push("/oegen");
    },
  },
};
</script>

<style scoped>
.oegen-detail-page {
  padding: 4px;
}

/* ===== 按钮统一精修：small 尺寸、圆润、精致 ===== */
/* 色彩语义：
   - 实心 primary（蓝）：每个区块唯一主 CTA（运行任务、查询、保存修改）
   - 描边 primary plain（蓝）：次要确认 / 特色功能（保存 prompt、贴入 rawdata）
   - 描边 danger plain（红）：破坏性操作（全部清除）
   - 描边 warning plain（橙）：警告性操作（导入会覆盖数据）
   - 默认灰：工具操作（刷新、下载、收起）
*/
.oegen-detail-page ::v-deep .el-button--small {
  border-radius: 4px;
  font-weight: 500;
  letter-spacing: 0.2px;
  transition: all 0.2s ease;
}

/* 默认按钮：补充 hover 状态，避免与彩色按钮对比时显得"死板" */
.oegen-detail-page ::v-deep .el-button--small:not(.is-plain):not(.el-button--primary):not(.el-button--success):not(.el-button--warning):not(.el-button--danger):hover {
  color: #409EFF;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
  transform: translateY(-1px);
}

.oegen-detail-page ::v-deep .el-button--small.is-plain:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.oegen-detail-page ::v-deep .el-button--text {
  font-weight: 500;
  letter-spacing: 0.2px;
}

.oegen-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f7fafe 100%);
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #ebeef5;
}

.oegen-detail-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.oegen-detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.oegen-detail-title i {
  color: #409EFF;
}

.oegen-detail-tid {
  color: #909399;
  font-weight: 400;
  font-size: 13px;
}

.oegen-detail-card {
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.oegen-detail-card ::v-deep .el-card__header {
  padding: 12px 18px;
  background: #fafbfc;
  border-bottom: 1px solid #ebeef5;
}

.oegen-detail-card ::v-deep .el-card__body {
  padding: 16px 18px;
}

.oegen-detail-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.oegen-detail-card-header i {
  color: #409EFF;
  font-size: 16px;
}

.oegen-detail-loading {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.oegen-detail-loading i {
  font-size: 32px;
  color: #409EFF;
}

.oegen-detail-loading p {
  font-size: 14px;
  margin-top: 10px;
}

/* ===== 运行控制：重新设计的排版 ===== */
.run-control {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.run-row {
  display: flex;
}

/* 第一行：模型字段 + 回答字数 + 操作按钮，按钮靠右 */
.run-row--action {
  align-items: flex-end;
  gap: 24px;
}

/* 第二行：Prompt 编辑区，占满宽度 */
.run-row--prompt {
  flex-direction: column;
  padding-top: 16px;
  border-top: 1px dashed #ebeef5;
}

.run-field {
  display: flex;
  flex-direction: column;
}

.run-field--model {
  width: 280px;
  flex-shrink: 0;
}

.run-field--word-count {
  width: 220px;
  flex-shrink: 0;
}

.run-field--prompt {
  flex: 1;
  min-width: 0;
}

/* 字段标签：小号、加粗、带图标，营造精致感 */
.run-field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
  user-select: none;
}

.run-field-label i {
  color: #409EFF;
  font-size: 14px;
}

.run-field-hint {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #c0c4cc;
  letter-spacing: 0;
}

/* Prompt 区头部：标签左、操作按钮右 */
.run-field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.run-model-select {
  width: 100%;
}

.run-action-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

/* ===== 回答字数：点击编辑交互 ===== */
.word-count-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.word-count-display:hover {
  background: #ecf5ff;
  border-color: #409EFF;
}

.word-count-display:hover .el-icon-edit {
  color: #409EFF;
}

.word-count-text {
  font-size: 13px;
  color: #c0c4cc;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.word-count-display .el-icon-edit {
  font-size: 14px;
  color: #c0c4cc;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.prompt-editor-actions {
  display: flex;
  gap: 8px;
}

/* Prompt 文本框：等宽字体，暗示其为模板内容 */
.run-prompt-input ::v-deep .el-textarea__inner {
  font-family: 'JetBrains Mono', 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
}

/* ===== Prompt 折叠态：精致状态条 ===== */
.prompt-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  user-select: none;
}

.prompt-summary:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e8eef5 100%);
  border-color: #409EFF;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.08);
}

.prompt-summary:hover .prompt-summary-arrow {
  transform: translateY(2px);
  color: #409EFF;
}

.prompt-summary:hover .prompt-summary-action {
  color: #409EFF;
}

.prompt-summary-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.prompt-summary-icon {
  color: #409EFF;
  font-size: 15px;
}

.prompt-summary-label {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.3px;
}

.prompt-summary-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2px;
  line-height: 1.4;
}

.prompt-summary-badge i {
  font-size: 12px;
}

.prompt-summary-badge.is-custom {
  background: rgba(103, 194, 58, 0.1);
  color: #67c23a;
  border: 1px solid rgba(103, 194, 58, 0.2);
}

.prompt-summary-badge.is-default {
  background: rgba(144, 147, 153, 0.1);
  color: #909399;
  border: 1px solid rgba(144, 147, 153, 0.2);
}

.prompt-summary-right {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  transition: color 0.25s ease;
}

.prompt-summary-arrow {
  font-size: 13px;
  transition: transform 0.25s ease, color 0.25s ease;
}

/* ===== Prompt 展开态过渡动画 ===== */
.prompt-expand-enter-active,
.prompt-expand-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.prompt-expand-enter,
.prompt-expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.oegen-progress-wrap {
  margin-top: 2px;
}

.oegen-progress-text {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

.oegen-result-toolbar {
  margin-left: auto;
  display: flex;
  align-items: center;
}

/* 失败筛选 chip：可点击切换，默认态为描边红框 + 警示图标，
   激活态（已筛选失败）变为实心红底白字 + 关闭图标，提示用户可点击清除 */
.oegen-failed-chip {
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2px;
  line-height: 1.4;
  color: #F56C6C;
  background: rgba(245, 108, 108, 0.08);
  border: 1px solid rgba(245, 108, 108, 0.3);
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.22s ease;
}

.oegen-failed-chip i {
  font-size: 13px;
  color: #F56C6C;
  transition: transform 0.22s ease;
}

.oegen-failed-chip:hover {
  background: rgba(245, 108, 108, 0.14);
  border-color: #F56C6C;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.18);
}

.oegen-failed-chip:hover i {
  transform: scale(1.12);
}

/* 激活态：实心红色，白字，关闭图标提示“点击清除筛选” */
.oegen-failed-chip.is-active {
  color: #fff;
  background: linear-gradient(135deg, #f87878 0%, #F56C6C 100%);
  border-color: #F56C6C;
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.4);
}

.oegen-failed-chip.is-active i {
  color: #fff;
}

.oegen-failed-chip.is-active:hover {
  background: linear-gradient(135deg, #f66161 0%, #ec5454 100%);
  box-shadow: 0 3px 10px rgba(245, 108, 108, 0.5);
}

.oegen-failed-chip-label {
  white-space: nowrap;
}

/* 失败状态标签：鼠标悬停显示 err_msg，指针光标 + 虚线下划线提示可交互 */
.oegen-status-failed {
  cursor: help;
  border-bottom: 1px dashed #F56C6C;
  transition: opacity 0.2s ease;
}

.oegen-status-failed:hover {
  opacity: 0.85;
}

.oegen-aitxt {
  color: #303133;
  line-height: 1.5;
}

.oegen-empty-cell {
  color: #c0c4cc;
}

.oegen-errmsg {
  color: #F56C6C;
  font-size: 12px;
}

.oegen-pagination {
  margin-top: 14px;
  text-align: right;
}

/* ===== 追问数据展开行 ===== */
/* 无追问数据的行隐藏展开图标，保持列宽一致 */
.oegen-detail-page ::v-deep .el-table .no-follow .el-table__expand-icon {
  visibility: hidden;
}

/* 有追问数据的行：左侧彩色竖条 + 浅橙背景，醒目区分 */
.oegen-detail-page ::v-deep .el-table .has-follow td {
  background: #fdf6ec !important;
  border-left: none;
  position: relative;
}

/* 第一列（展开列）添加左侧彩色竖条 */
.oegen-detail-page ::v-deep .el-table .has-follow td:first-child {
  box-shadow: inset 3px 0 0 0 #e6a23c;
}

/* 展开图标染色为橙色，提示可展开 */
.oegen-detail-page ::v-deep .el-table .has-follow .el-table__expand-icon {
  color: #e6a23c;
  font-weight: 600;
}

.oegen-detail-page ::v-deep .el-table .has-follow .el-table__expand-icon:hover {
  color: #cf9236;
}

/* 展开区背景与主表区分 */
.oegen-detail-page ::v-deep .el-table__expanded-cell {
  padding: 12px 16px;
  background: #fafbfc;
}

/* ===== 题号单元格：vid + 追问条数徽章 ===== */
.oegen-vid-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.oegen-vid-text {
  font-weight: 600;
  color: #303133;
}

/* 追问条数徽章：橙底白字小胶囊，强提示 */
.oegen-follow-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: linear-gradient(135deg, #f0b65a 0%, #e6a23c 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border-radius: 9px;
  box-shadow: 0 1px 3px rgba(230, 162, 60, 0.4);
  user-select: none;
}

.oegen-follow-badge i {
  font-size: 10px;
}

.oegen-follow-wrap {
  padding: 4px 0;
}

.oegen-follow-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.oegen-follow-title i {
  color: #e6a23c;
  font-size: 15px;
}

.oegen-follow-count {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
  background: #f0f2f5;
  border-radius: 10px;
  padding: 1px 8px;
}

.oegen-follow-empty {
  text-align: center;
  padding: 16px;
  color: #c0c4cc;
  font-size: 13px;
}

.oegen-follow-empty i {
  margin-right: 4px;
}

/* ===== 行内操作按钮 ===== */
.oegen-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.oegen-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #e4e7ed;
  border-radius: 5px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.22s ease;
  padding: 0;
  outline: none;
}

.oegen-action-btn i {
  font-size: 13px;
}

.oegen-action-btn--edit:hover {
  color: #409EFF;
  border-color: #409EFF;
  background: #ecf5ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.18);
}

.oegen-action-btn--delete:hover {
  color: #F56C6C;
  border-color: #F56C6C;
  background: #fef0f0;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.18);
}
</style>

<!-- 非 scoped 样式：用于 append-to-body 的对话框（对话框挂载到 body，scoped 样式无法穿透） -->
<style>
.oegen-edit-dialog {
  border-radius: 10px !important;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12) !important;
}

.oegen-edit-dialog .el-dialog__header {
  padding: 0;
  border-bottom: none;
}

.oegen-edit-dialog .el-dialog__body {
  padding: 24px 28px 8px;
}

.oegen-edit-dialog .el-dialog__footer {
  padding: 12px 28px 20px;
  border-top: 1px solid #f0f2f5;
}

.oegen-edit-dialog-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  border-bottom: 1px solid #ebeef5;
}

.oegen-edit-dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 18px;
  color: #fff;
  flex-shrink: 0;
}

.oegen-edit-dialog-icon--main {
  background: linear-gradient(135deg, #4da3ff 0%, #409EFF 100%);
  box-shadow: 0 3px 10px rgba(64, 158, 255, 0.35);
}

.oegen-edit-dialog-icon--follow {
  background: linear-gradient(135deg, #f0b65a 0%, #e6a23c 100%);
  box-shadow: 0 3px 10px rgba(230, 162, 60, 0.35);
}

.oegen-edit-dialog-title {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.oegen-edit-dialog-title-main {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.3px;
}

.oegen-edit-dialog-title-sub {
  font-size: 12px;
  color: #909399;
  letter-spacing: 0.2px;
}

.oegen-edit-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.oegen-edit-field {
  display: flex;
  flex-direction: column;
}

.oegen-edit-field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  letter-spacing: 0.3px;
}

.oegen-edit-field-label i {
  color: #409EFF;
  font-size: 14px;
}

.oegen-edit-textarea .el-textarea__inner {
  font-size: 13px;
  line-height: 1.6;
  border-radius: 6px;
  padding: 10px 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;
}

.oegen-edit-textarea .el-textarea__inner:focus {
  border-color: #409EFF;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.oegen-edit-field-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.oegen-edit-char-count {
  font-size: 11px;
  color: #c0c4cc;
  letter-spacing: 0.2px;
}

.oegen-edit-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* ===== 模型下拉分组标题样式（供应商分割） ===== */
.oegen-model-popper .el-select-group__title {
  font-size: 12px;
  color: #909399;
  font-weight: 600;
  padding-left: 12px;
  background: #fafbfc;
}
</style>
